/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useUser } from '../hooks/UNSAFE_useUser';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { useEffect, useRef, useState } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { CircleWrapper, CircleInner } from '../PRIVATE_Meter';
import { CircularReferenceLine } from './CircularReferenceLine';

import {
  validateRange,
  getTrackAndIndicatorColor,
  getMeterAriaProps,
  getCircleSection
} from '../utils/PRIVATE_meterUtils';

import { getDimensions, getValue, getCenterContext } from './CircleMeterUtils';

import {
  getIndicatorProps,
  getTrackProps,
  getThresholdsProps,
  getReferenceLinesProps,
  MeterInfo
} from './LayoutUtils';

import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '../hooks/PRIVATE_useDvtMeterEvents';

import { MeterProps, CircularRefLine } from './MeterCircle.types';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';

/**
 * A Meter circle displays information graphically in a circular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function MeterCircle({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  size = 'lg',
  startAngle = 90,
  angleExtent = 360,
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  ...props
}: MeterProps) {
  validateRange(min, max, value, step);

  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const section = getCircleSection(startAngle, angleExtent, isRtl);
  const dimensionsRef = useRef(getDimensions(null, section));
  const circleWrapperRef = useRef<HTMLDivElement>(null);
  const [isDimsReady, setDimsReady] = useState<boolean>(false);

  useEffect(() => {
    if (props.onCommit || props.onInput || props.children) {
      dimensionsRef.current = getDimensions(circleWrapperRef.current, section);
      setDimsReady(true);
    }
  }, [size, props.onCommit, props.onInput, props.children]);

  const getValueFromEvent = (event: PointerEvent) => {
    const circleContainer = circleWrapperRef.current;
    if (event.target == circleContainer) {
      return getValue(
        event,
        min,
        max,
        step,
        dimensionsRef,
        startAngle,
        angleExtent,
        section,
        isRtl
      );
    }
    return;
  };

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    circleWrapperRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    props.datatip,
    props.ariaDescribedBy
  );

  const ariaProps = getMeterAriaProps(
    value,
    min,
    max,
    `${value}`,
    props.accessibleLabel,
    props.ariaLabelledBy,
    props.thresholds
  );
  const mergedProps = mergeProps(pointerEventsProps, keyboardEventsProps, datatipProps, ariaProps);

  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const orientation =
    section === 'bottom' || section === 'top'
      ? 'Horizontal'
      : section === 'left' || section === 'right'
      ? 'Vertical'
      : 'Full';

  const meterInfo = {
    min,
    max,
    value,
    startAngle,
    angleExtent,
    size,
    section,
    isRtl,
    innerRadius: dimensionsRef.current.innerRadius
  } as MeterInfo;
  const isInteractive = props.onCommit || props.onInput;
  const { isTabbable } = useTabbableMode();
  const centerContext = getCenterContext(dimensionsRef, section);
  return (
    <>
      <div
        class={classNames([component.base, isInteractive ? component.interactive : ''])}
        tabIndex={!isTabbable ? -1 : 0}
        role="slider"
        {...mergedProps}>
        <CircleWrapper
          ref={circleWrapperRef}
          class={classNames([
            circleWrapper.base,
            props.referenceLines && props.referenceLines.length > 0
              ? styles[`${size}${section}`]
              : '',
            styles[`${size}${orientation}`]
          ])}>
          <CircleInner
            class={classNames([circleTrack.base, styles[section]])}
            {...getTrackProps(
              meterInfo,
              isTrackRendered,
              thresholdDisplay,
              trackColor,
              props.innerRadius,
              props.thresholds
            )}
          />
          {thresholdDisplay === 'all' &&
            props.thresholds &&
            getThresholdsProps(
              meterInfo,
              props.thresholds,
              isTrackRendered,
              trackColor,
              props.innerRadius
            ).map((threshold) => {
              return (
                <CircleInner
                  class={classNames([circleTrack.base, styles[section], styles.thresholds])}
                  {...threshold}
                />
              );
            })}

          <CircleInner
            color={indicatorColor}
            class={classNames([circleValue.base, circleValue[section], styles[section]])}
            {...getIndicatorProps(meterInfo, indicatorSize, props.innerRadius)}
          />
          {props.referenceLines?.map((refLine: CircularRefLine) => {
            return (
              <CircularReferenceLine
                {...getReferenceLinesProps(meterInfo, refLine, props.innerRadius)}
                section={section}
              />
            );
          })}
          {isDimsReady && props.children && centerContext && (
            <div class={classNames([styles.centerContent, childrenStyles])}>
              {props.children(centerContext)}
            </div>
          )}
        </CircleWrapper>
      </div>
      {datatipContent}
    </>
  );
}

const component = {
  base: cssProps`${{
    display: 'inline-block',
    pointerEvents: 'none'
  }}`,
  interactive: cssProps`${{
    touchAction: 'none',
    '&:focus-visible': {
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
      outlineStyle: 'dotted',
      outlineWidth: '1px'
    }
  }}`
};

const styles = {
  full: cssProps`${{
    borderRadius: '50%'
  }}`,
  top: cssProps`${{
    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
    borderBottom: '0'
  }}`,
  bottom: cssProps`${{
    borderRadius: '0% 0% 50% 50% / 0% 0% 100% 100%',
    borderTop: '0'
  }}`,
  right: cssProps`${{
    borderRadius: '0 100% 100% 0 / 0% 50% 50% 0%',
    borderLeft: '0'
  }}`,
  left: cssProps`${{
    borderRadius: '100% 0 0 100% / 50% 0 0 50%',
    borderRight: '0'
  }}`,
  smtop: cssProps`${{
    margin: '1rem 1rem 0 1rem'
  }}`,
  mdtop: cssProps`${{
    margin: '0.75rem 0.75rem 0 0.75rem'
  }}`,
  lgtop: cssProps`${{
    margin: '0.5rem 0.5rem 0 0.5rem'
  }}`,
  smbottom: cssProps`${{
    margin: '0 1rem 1rem 1rem'
  }}`,
  mdbottom: cssProps`${{
    margin: '0 0.75rem 0.75rem 0.75rem'
  }}`,
  lgbottom: cssProps`${{
    margin: '0 0.5rem 0.5rem 0.5rem'
  }}`,
  smleft: cssProps`${{
    margin: '1rem 0 1rem 1rem'
  }}`,
  mdleft: cssProps`${{
    margin: '0.75rem 0 0.75rem 0.75rem'
  }}`,
  lgleft: cssProps`${{
    margin: '0.5rem 0 0.5rem 0.5rem'
  }}`,
  smright: cssProps`${{
    margin: '1rem 1rem 1rem 0'
  }}`,
  mdright: cssProps`${{
    margin: '0.75rem 0.75rem 0.75rem 0'
  }}`,
  lgright: cssProps`${{
    margin: '0.5rem 0.5rem 0.5rem 0'
  }}`,
  smfull: cssProps`${{
    margin: '1rem'
  }}`,
  mdfull: cssProps`${{
    margin: '0.75rem'
  }}`,
  lgfull: cssProps`${{
    margin: '0.5rem'
  }}`,
  lgHorizontal: cssProps`${{
    height: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size) / 2 )',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size)'
  }}`,
  lgVertical: cssProps`${{
    width: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size) / 2 )',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size)'
  }}`,
  smHorizontal: cssProps`${{
    height: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size) / 2 )',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size)'
  }}`,
  smVertical: cssProps`${{
    width: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size) / 2 )',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size)'
  }}`,
  mdHorizontal: cssProps`${{
    height: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size) / 2 )',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size)'
  }}`,
  mdVertical: cssProps`${{
    width: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size) / 2 )',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size)'
  }}`,
  smFull: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-sm-size)'
  }}`,
  mdFull: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-md-size)'
  }}`,
  lgFull: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-lg-size)'
  }}`,
  centerContent: cssProps`${{
    pointerEvents: 'none',
    width: '100%',
    height: '100%',
    lineHeight: 'initial'
  }}`,
  thresholds: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-threshold-color)'
  }}`
};
const childrenStyles = cssProps`${{
  /* @ts-ignore */
  '& > *': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important'
  }
}}`;

const circleWrapper = {
  base: cssProps`${{
    position: 'relative',
    boxSizing: 'border-box',
    cursor: 'pointer',
    pointerEvents: 'auto'
  }}`
};

const circleTrack = {
  base: cssProps`${{
    width: '100%',
    height: '100%',
    position: 'absolute',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-track-bg-color)'
  }}`,
  sm: cssProps`${{
    borderWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-sm-track-size)'
  }}`,
  md: cssProps`${{
    borderWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-md-track-size)'
  }}`,
  lg: cssProps`${{
    borderWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-lg-track-size)'
  }}`
};

const circleValue = {
  base: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-value-bg-color)',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    position: 'absolute',
    pointerEvents: 'none'
  }}`,
  full: cssProps`${{
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    translate: '-50% -50%'
  }}`,
  top: cssProps`${{
    top: '100%',
    left: '50%',
    translate: '-50% -100%'
  }}`,
  right: cssProps`${{
    top: '50%',
    translate: '0 -50%'
  }}`,
  left: cssProps`${{
    left: '100%',
    top: '50%',
    translate: '-100% -50%'
  }}`,
  bottom: cssProps`${{
    left: '50%',
    translate: '-50% 0'
  }}`
};
